import { useState, useEffect, useRef, useMemo } from "react";
import ReactDOM from "react-dom";
import { TextField } from "../common/inputFiled";
import { PhoneField } from "../common/phoneFiled";
import { CountryModel } from "../common/countryModel";
import toast from "react-hot-toast";
import Cookies from "js-cookie";
import { LocationModal } from "./locationModel";
import { LocationModel } from "./locationModel";
// Mappls picker kept for future rollback — switch import below to re-enable.
// import { MapplsMapPicker } from "./mapplsMapPicker";
import { GoogleMapPicker } from "./googleMapPicker";
import usePaymentStatus from "../../hooks/usePaymentStatus";
import { getPlanDisplayName } from "../../services/razorpayService";
import { ViewColleagueModal } from "./viewcolleaguemodal";

// ─── Module-level constants ────────────────────────────────────────────────────

const CAPITALIZE_FIELDS = new Set([
  "company_name", "job_title", "job_description",
  "building_name", "floor_and_office_number",
  "work_address_street_name", "city_region_country",
]);

const isValidUrl = (val) => {
  const text = String(val || "").trim();
  if (!text) return true;
  if (/\s/.test(text)) return false;
  return /^(?:www\.|https:\/\/(?:www\.)?)[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*\.[a-zA-Z]{2,10}(?:\/\S*)?$/i.test(text);
};

const onlyDigits = (val) => String(val).replace(/\D+/g, "");

const isPlusCodePart = (value = "") =>
  /^[A-Z0-9]{4,}\+[A-Z0-9]{2,}/i.test(String(value || "").trim());

const cleanMapDetail = (value = "") => {
  const text = String(value || "").trim();
  return isPlusCodePart(text) ? "" : text;
};

const deriveStreetFromAddress = (address = "") => {
  const parts = String(address || "")
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean)
    .filter((part) => !isPlusCodePart(part));
  return parts[0] || "";
};

const INITIAL_FORM = {
  experience_id: "",
  company_website: "",
  job_function: "",
  job_sub_functions: "",
  job_title: "",
  building_name: "",
  job_description: "",
  work_start_date: "",
  work_end_date: "",
  company_name: "",
  work_experience_pin_code: "",
  work_address_pin_code: "",
  work_address_google_map: "",
  work_address_latitude: "",
  work_address_longitude: "",
  floor_and_office_number: "",
  work_address_street_name: "",
  work_address_area_locality: "",
  work_address_city: "",
  work_address_region: "",
  work_address_country: "",
  city_region_country: "",
  contact_number: "",
  work_current_job: "0",
};

// Fields visible in the UI for plan 2+
const WORK_DETAILS_FIELDS = [
  "company_name",
  "work_experience_pin_code",
  "city_region_country",
  "job_title",
  "work_start_date",
  "work_end_date",
  "job_function",
  "job_sub_functions",
  "job_description",
];

const WORK_ADDRESS_PLAN2_FIELDS = [
  "company_website",
];

const WORK_ADDRESS_PLAN3_FIELDS = [
  "floor_and_office_number",
  "building_name",
  "work_address_street_name",
  "work_address_pin_code",
  "work_address_google_map",
  "company_website",
];

// Additional UI fields visible only for plan 3
const PLAN3_EXTRA_FIELDS = new Set([
  "floor_and_office_number", "building_name", "work_address_google_map",
  "work_address_city", "work_address_region", "work_address_country",
]);

// Required fields per plan (for validation)
const REQUIRED_PLAN2 = new Set([
  "company_name", "job_function", "job_sub_functions", "job_title",
  "work_start_date", "work_end_date", "work_experience_pin_code", "city_region_country",
  "work_address_city", "work_address_region", "work_address_country",
]);

const REQUIRED_PLAN3_EXTRA = new Set([
  "floor_and_office_number", "building_name", "work_address_google_map",
]);

// What actually gets sent in the API payload per plan
// (excludes: latitude, longitude, street_name, area_locality, contact_number — all Not Applicable)
const PAYLOAD_PLAN2 = new Set([
  "experience_id",
  "company_name", "job_function", "job_sub_functions", "job_title",
  "work_start_date", "work_end_date", "job_description", "company_website",
  "work_experience_pin_code", "work_address_pin_code", "city_region_country",
  "work_address_city", "work_address_region", "work_address_country",
]);

const PAYLOAD_PLAN3_EXTRA = new Set([
  "floor_and_office_number", "building_name", "work_address_google_map",
  "work_address_city", "work_address_region", "work_address_country",
  "work_address_latitude", "work_address_longitude",
]);

const JOB_FUNCTION_OPTIONS = [
  { value: "Founder/Owner/Chairman/Managing Director", label: "Founder/Owner/Chairman/Managing Director" },
  { value: "Business Group Head", label: "Business Group Head" },
  { value: "Business Unit Head", label: "Business Unit Head" },
  { value: "Business Function Head", label: "Business Function Head" },
  { value: "Managing Managers", label: "Managing Managers" },
  { value: "Manager", label: "Manager" },
  { value: "Individual Contributor", label: "Individual Contributor" },
];

const JOB_SUB_FUNCTION_OPTIONS = [
  { value: "General Manager", label: "General Manager" },
  { value: "Finance", label: "Finance" },
  { value: "Human Resource", label: "Human Resource" },
  { value: "Marketing", label: "Marketing" },
  { value: "Sales", label: "Sales" },
  { value: "Production", label: "Production" },
  { value: "Procurement", label: "Procurement" },
  { value: "Administration", label: "Administration" },
  { value: "Supply Chain", label: "Supply Chain" },
  { value: "Information Technology", label: "Information Technology" },
];

// Ordered field definitions — filtered by plan at render time
const FIELD_DEFS = [
  { key: "company_name", label: "Company Name" },
  { key: "work_experience_pin_code", label: "Pincode", type: "number" },
  { key: "city_region_country", label: "City, Region and Country" },
  { key: "job_title", label: "Job Title" },
  { key: "work_start_date", label: "Start Date", type: "month" },
  { key: "work_end_date", label: "End Date", type: "month" },
  { key: "job_function", label: "Job Function", type: "select", options: JOB_FUNCTION_OPTIONS },
  { key: "job_sub_functions", label: "Job Sub-Function", type: "select", options: JOB_SUB_FUNCTION_OPTIONS },
  { key: "job_description", label: "Description" },
  // Plan 3 only
  { key: "floor_and_office_number", label: "Building Number/Office Number" },
  { key: "building_name", label: "Building Name" },
  { key: "work_address_google_map", label: "Address" },
  { key: "work_address_pin_code", label: "Pincode", type: "number" },
  { key: "work_address_street_name", label: "Street Name" },
  { key: "work_address_area_locality", label: "Area/Locality" },
  { key: "work_address_city", label: "City" },
  { key: "work_address_region", label: "Region" },
  { key: "work_address_country", label: "Country" },
  { key: "company_website", label: "Website" },
];

const AUTO_FILLED_WORK_ADDRESS_FIELDS = new Set([
  "work_address_city",
  "work_address_region",
  "work_address_country",
]);

const HIDDEN_WORK_ADDRESS_FIELDS = new Set([
  "work_address_pin_code",
  "work_address_street_name",
]);

// ─── Component ─────────────────────────────────────────────────────────────────

export const WorkExperiencePage = ({ onSave }) => {
  const { planType, isPaymentActive, loading: paymentStatusLoading } = usePaymentStatus();
  const token = useRef(Cookies.get("auth_token")).current;
  const [isEditing, setIsEditing] = useState(false);
  const [openWorkDetails, setOpenWorkDetails] = useState(true);
  const [openWorkAddress, setOpenWorkAddress] = useState(true);
  const [addExperiance, setAddExperiance] = useState(false);
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [showGoogleMapPicker, setShowGoogleMapPicker] = useState(false);
  const [mapInitialSearch, setMapInitialSearch] = useState("");
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedExperience, setSelectedExperience] = useState(null);
  const [errors, setErrors] = useState({});
  const [imagePreview, setImagePreview] = useState(null);
  const [experiences, setExperiences] = useState([]);
  const [loadingExperiences, setLoadingExperiences] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [dateError, setDateError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [showCountryModal, setShowCountryModal] = useState(false);
  const [selectedCountryIso, setSelectedCountryIso] = useState("");
  const [deleteModal, setDeleteModal] = useState({ open: false, experienceId: null });

  const formScrollRef = useRef(null);
  const fieldRefs = useRef({});
  const lastPincodeToastRef = useRef(0);

  const requiredKeys = useMemo(() => {
    if (planType >= 3) return new Set([...REQUIRED_PLAN2, ...REQUIRED_PLAN3_EXTRA]);
    return REQUIRED_PLAN2;
  }, [planType]);

  const visibleWorkDetailsFields = useMemo(() => {
    if (planType < 2) return [];
    return WORK_DETAILS_FIELDS;
  }, [planType]);

  const visibleWorkAddressFields = useMemo(() => {
    if (planType < 2) return [];
    if (planType === 2) return WORK_ADDRESS_PLAN2_FIELDS;
    const hasGoogleMapValue = Boolean(String(formData.work_address_google_map || "").trim());
    if (hasGoogleMapValue) {
      return WORK_ADDRESS_PLAN3_FIELDS.filter((key) => ![
        "work_address_city",
        "work_address_region",
        "work_address_country",
      ].includes(key));
    }
    return WORK_ADDRESS_PLAN3_FIELDS;
  }, [planType, formData.work_address_google_map]);

  const isPincodeDisabledByAddress = planType >= 3 && Boolean(String(formData.work_address_google_map || "").trim());

  useEffect(() => {
    fetchExperiences();
  }, []);

  const fetchExperiences = async () => {
    setLoadingExperiences(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/get_work_experience`, {
        method: "GET",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();

      if (data.status === "1" && Array.isArray(data.data?.[0]?.work_experience)) {
        const updatedList = await Promise.all(
          data.data[0].work_experience.map(async (exp) => {
            try {
              const contactRes = await fetch(
                `${process.env.REACT_APP_API_BASE_URL}/get_contact_add_work_experience`,
                {
                  method: "POST",
                  headers: {
                    "Content-Type": "application/x-www-form-urlencoded",
                    Authorization: `Bearer ${token}`,
                  },
                  body: new URLSearchParams({ experience_id: exp.experience_id }).toString(),
                }
              );
              const contactData = await contactRes.json();
              return { ...exp, contacts: contactData.status === "1" ? contactData.data || [] : [] };
            } catch {
              return { ...exp, contacts: [] };
            }
          })
        );
        setExperiences(updatedList);
      } else {
        toast.dismiss();
        toast.error(data.message || "No experience found");
      }
    } catch (err) {
      toast.dismiss();
      toast.error(err.message);
    } finally {
      setLoadingExperiences(false);
    }
  };

  const handleChange = (field, value) => {
    if (field === "work_current_job") {
      setFormData((prev) => ({
        ...prev,
        [field]: value,
        work_end_date: value === 1 ? "" : prev.work_end_date,
      }));
      return;
    }
    const titleCaseFields = new Set(["company_name", "job_title", "building_name"]);
    let normalizedValue = typeof value === "string" ? value.replace(/^\s+/, "") : value;
    const toTitleCase = (str) => {
      if (!str) return str;
      return str
        .split(" ")
        .map((word) => (word ? word.charAt(0).toUpperCase() + word.slice(1) : ""))
        .join(" ");
    };

    const websiteNoSpaceValue = field === "company_website" ? String(normalizedValue || "").replace(/\s+/g, "") : normalizedValue;
    const processedValue = titleCaseFields.has(field) && websiteNoSpaceValue ? toTitleCase(websiteNoSpaceValue) : websiteNoSpaceValue;
    setFormData((prev) => {
      const next = { ...prev, [field]: processedValue };
      if (field === "work_experience_pin_code") next.work_address_pin_code = processedValue;
      if (field === "work_address_pin_code") next.work_experience_pin_code = processedValue;
      return next;
    });
  };

  const scrollToFirstError = (errs) => {
    const firstKey = Object.keys(errs)[0];
    if (!firstKey) return; // No errors, don't scroll or show toast
    
    const el = fieldRefs.current[firstKey];
    const container = formScrollRef.current;
    if (el) {
      const input = el.querySelector("input, select, textarea");
      if (container) {
        const offsetTop = el.offsetTop - 24;
        container.scrollTo({ top: offsetTop < 0 ? 0 : offsetTop, behavior: "smooth" });
      } else {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      }
      if (input && typeof input.focus === "function") input.focus();
    }
    toast.error("Please fix the highlighted fields");
  };

  const handleSubmit = async () => {
    if (submitting) return;
    const newErrors = {};
    const isCurrentJob = Number(formData.work_current_job) === 1;
    for (const key of requiredKeys) {
      if (isCurrentJob && key === "work_end_date") continue;
      if (!formData[key] || String(formData[key]).trim() === "") {
        const def = FIELD_DEFS.find((f) => f.key === key);
        newErrors[key] = `Enter ${def?.label || key}`;
      }
    }

    if (formData.company_website && !isValidUrl(formData.company_website))
      newErrors.company_website = "Enter a valid website";

    if (formData.work_experience_pin_code || formData.work_address_pin_code) {
      const pinVal = formData.work_experience_pin_code || formData.work_address_pin_code;
      if (onlyDigits(pinVal).length !== 6)
        newErrors.work_experience_pin_code = "Pin Code must be exactly 6 digits";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      scrollToFirstError(newErrors);
      return;
    }

    if (
      !isCurrentJob &&
      formData.work_start_date &&
      formData.work_end_date &&
      new Date(formData.work_end_date) < new Date(formData.work_start_date)
    ) {
      const msg = "Ending date cannot be before starting date";
      setErrors((prev) => ({ ...prev, work_end_date: msg }));
      setDateError(msg);
      toast.error(msg);
      return;
    }

    setSubmitting(true);
    try {
      // Build payload with only plan-appropriate fields
      // Convert month input (YYYY-MM) to MM/YYYY for API payload
      const toPayloadMonth = (val) => {
        if (!val && val !== 0) return "";
        const s = String(val);
        if (/^\d{4}-\d{2}$/.test(s)) {
          const [y, m] = s.split("-");
          return `${m}/${y}`;
        }
        // Already in MM/YYYY or other formats - return as-is
        return s;
      };

      const temp = { ...formData };
      if (temp.work_start_date) temp.work_start_date = toPayloadMonth(temp.work_start_date);
      temp.work_end_date = isCurrentJob ? "" : temp.work_end_date ? toPayloadMonth(temp.work_end_date) : "";

      const payload = new URLSearchParams();
      payload.set("work_current_job", temp.work_current_job || "0");
      for (const key of PAYLOAD_PLAN2) {
        if (planType >= 2) payload.set(key, temp[key] || "");
      }
      if (planType >= 3) {
        for (const key of PAYLOAD_PLAN3_EXTRA) {
          payload.set(key, temp[key] || "");
        }
      }

      const endpoint = selectedId
        ? `${process.env.REACT_APP_API_BASE_URL}/update_work_experience`
        : `${process.env.REACT_APP_API_BASE_URL}/insert_work_experience`;

      const res = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          Authorization: `Bearer ${token}`,
        },
        body: payload.toString(),
      });
      const data = await res.json();

      if (data.status === "1") {
        toast.success(selectedId ? "Experience updated!" : "Experience added!");
        setErrors({});
        setAddExperiance(false);
        setIsEditing(false);
        setSelectedId(null);
        fetchExperiences();
        if (typeof onSave === "function") onSave();
      } else {
        toast.dismiss();
        toast.error(data.message || "Failed to save experience");
      }
    } catch (err) {
      toast.dismiss();
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  // Step 1: user picks a place from LocationModal
  const handlePlaceSelect = ({ place, location, country, post_code }) => {
    const placeName = place?.place_name || place?.["place name"] || place?.city || location?.city || "";
    const state = place?.state || place?.state_abbreviation || location?.state || "";
    const countryName = location?.city || placeName || "";
    const latitude = place?.latitude || location?.places?.[0]?.latitude || "";
    const longitude = place?.longitude || location?.places?.[0]?.longitude || "";
    setShowLocationModal(false);

    setFormData((prev) => ({
      ...prev,
      city_region_country: [countryName, state, country].filter(Boolean).join(", "),
      work_experience_pin_code: post_code || "",
      work_address_pin_code: post_code || "",
      work_address_city: countryName || "",
      work_address_region: state || "",
      work_address_country: country || "",
      work_address_latitude: latitude || "",
      work_address_longitude: longitude || "",
    }));
    setErrors((prev) => ({
      ...prev,
      city_region_country: null,
      work_experience_pin_code: null,
      work_address_pin_code: null,
      work_address_city: null,
      work_address_region: null,
      work_address_country: null,
      work_address_latitude: null,
      work_address_longitude: null,
    }));
    toast.success("Work address updated from location!");
  };

  // Step 2: user confirms pin on Google Map
  const handleSelectLocation = (item) => {
    const previousPin = String(formData.work_experience_pin_code || formData.work_address_pin_code || "");
    const selectedPin = String(item?.pin_code || "");
    const pinChanged = Boolean(selectedPin) && selectedPin !== previousPin;
    const addressValue = item?.formatted_address || item?.google_map_link || "";
    const cityRegionCountry =
      [item?.city, item?.region, item?.country].filter(Boolean).join(", ") ||
      item?.city_region_country ||
      item?.formatted_address ||
      "";
    setFormData((prev) => ({
      ...prev,
      work_experience_pin_code: selectedPin || prev.work_experience_pin_code,
      work_address_pin_code: selectedPin || prev.work_address_pin_code,
      work_address_google_map: addressValue,
      work_address_latitude: item?.latitude ? String(item.latitude) : "",
      work_address_longitude: item?.longitude ? String(item.longitude) : "",
      work_address_street_name: cleanMapDetail(item?.street_name) || deriveStreetFromAddress(addressValue),
      floor_and_office_number: cleanMapDetail(item?.flat_number),
      building_name: cleanMapDetail(item?.building_name),
      work_address_area_locality: item?.area_locality || "",
      work_address_city: item?.city || "",
      work_address_region: item?.region || "",
      work_address_country: item?.country || "",
      city_region_country: cityRegionCountry,
    }));
    if (pinChanged) {
      toast.success(`Work address updated. Pincode changed to ${selectedPin}.`);
    } else {
      toast.success("Work address updated from location!");
    }
    setShowGoogleMapPicker(false);
    setMapInitialSearch("");
  };

  const handleAddClick = () => {
    setSelectedId(null);
    setFormData(INITIAL_FORM);
    setErrors({});
    setDateError("");
    setAddExperiance(true);
    setIsEditing(true);
    setShowLocationModal(false);
    setShowCountryModal(false);
    setSelectedCountryIso("");
  };

  const handleCardClick = (exp) => {
    const toInputMonth = (val) => {
      if (!val && val !== 0) return "";
      // If already YYYY-MM (input month), return as-is
      if (/^\d{4}-\d{2}$/.test(String(val))) return String(val);
      // If MM/YYYY -> convert to YYYY-MM
      if (/^\d{2}\/\d{4}$/.test(String(val))) {
        const [m, y] = String(val).split("/");
        return `${y}-${m.padStart(2, "0")}`;
      }
      // If YYYY (year only)
      if (/^\d{4}$/.test(String(val))) return `${String(val)}-01`;
      return "";
    };

    const normalized = {
      ...exp,
      work_experience_pin_code: exp?.work_experience_pin_code || exp?.work_address_pin_code || "",
      work_current_job: exp?.work_current_job ?? "",
      work_start_date: toInputMonth(exp?.work_start_date),
      work_end_date: Number(exp?.work_current_job) === 1 ? "" : toInputMonth(exp?.work_end_date),
    };

    setSelectedId(exp.experience_id);
    setFormData(normalized);
    setErrors({});
    setDateError("");
    setAddExperiance(true);
    setIsEditing(false);
    setShowLocationModal(false);
    setShowCountryModal(false);
    setSelectedCountryIso("");
  };

  const handleToggleCurrentSelect = () => {
    const isCurrent = Number(formData.work_current_job) === 1;
    handleChange("work_current_job", isCurrent ? 0 : 1);
  };

  const openGoogleMapFromAddressField = () => {
    if (!isEditing) return;
    const seed = [
      formData.work_experience_pin_code || formData.work_address_pin_code,
      formData.city_region_country,
      formData.work_address_google_map,
    ].filter(Boolean).join(", ");
    setMapInitialSearch(seed);
    setShowGoogleMapPicker(true);
  };

  const handleDelete = (e, experienceId) => {
    e.stopPropagation();
    setDeleteModal({ open: true, experienceId });
  };

  const confirmDelete = async () => {
    const { experienceId } = deleteModal;
    setDeleteModal({ open: false, experienceId: null });
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/delete_work_experience`, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          Authorization: `Bearer ${token}`,
        },
        body: new URLSearchParams({ experience_id: experienceId }).toString(),
      });
      const data = await res.json();
      if (data.status === "1") {
        toast.success("Work experience deleted successfully!");
        fetchExperiences();
      } else {
        toast.error(data.message || "Failed to delete experience");
      }
    } catch {
      toast.error("Failed to delete experience");
    }
  };

  const getInitials = (name = "") => {
    const parts = name.trim().split(" ");
    if (parts.length === 1) return parts[0][0]?.toUpperCase() || "U";
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  };

  const formatWorkAddress = (exp) => {
    const addressParts = [
      cleanMapDetail(exp?.building_name),
      cleanMapDetail(exp?.floor_and_office_number),
      cleanMapDetail(exp?.work_address_street_name),
      exp?.work_address_area_locality,
      exp?.work_address_city,
      exp?.work_address_region,
      exp?.work_address_country,
      exp?.work_address_pin_code || exp?.work_experience_pin_code,
    ]
      .map((value) => String(value || "").trim())
      .filter(Boolean);

    if (addressParts.length > 0) {
      return addressParts.join(", ");
    }

    return exp?.city_region_country || exp?.work_address_google_map || "—";
  };

  const formatWorkDuration = (exp) => {
    const monthNames = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];

    const parseMonth = (value) => {
      const text = String(value || "").trim();
      if (!text) return null;

      let year, month;
      if (text.includes("-")) {
        const [y, m] = text.split("-");
        year = Number(y);
        month = Number(m);
      } else if (text.includes("/")) {
        const [m, y] = text.split("/");
        year = Number(y);
        month = Number(m);
      } else if (/^\d{4}$/.test(text)) {
        year = Number(text);
        month = 1;
      } else {
        return null;
      }

      if (!Number.isNaN(year) && !Number.isNaN(month) && month >= 1 && month <= 12) {
        return { year, month };
      }
      return null;
    };

    const fmt = (m) => `${(monthNames[m.month - 1] ?? "").slice(0,3)} ${m.year}`;

    const start = parseMonth(exp?.work_start_date);
    const end = parseMonth(exp?.work_end_date);

    if (!start && !end) return "—";

    if (Number(exp?.work_current_job) === 1) {
      if (!start) return "—";
      return `${fmt(start)} - Present`;
    }

    if (start && end) {
      return `${fmt(start)} - ${fmt(end)}`;
    }

    if (start && !end) return `${fmt(start)} - —`;
    if (!start && end) return `— - ${fmt(end)}`;
    return "—";
  };

  const hasVisibleFieldValue = (fieldKey) => {
    const value = formData[fieldKey];
    return String(value ?? "").trim() !== "";
  };

  const hasPreviewableWorkAddressField = (key) =>
    !HIDDEN_WORK_ADDRESS_FIELDS.has(key) && hasVisibleFieldValue(key);

  const shouldShowWorkAddressSection =
    isEditing || visibleWorkAddressFields.some(hasPreviewableWorkAddressField);

  const getPreviewIcon = (key) => {
    if (["work_start_date", "work_end_date", "work_current_job"].includes(key)) return "../input-logos/calander.svg";
    if (["work_experience_pin_code", "work_address_pin_code", "city_region_country", "work_address_google_map"].includes(key)) return "../input-logos/location.svg";
    if (["floor_and_office_number", "building_name", "work_address_street_name"].includes(key)) return "../input-logos/building.svg";
    return "../input-logos/user.svg";
  };

  const formatPreviewValue = (field, overrideValue) => {
    if (overrideValue !== undefined) return overrideValue;
    const value = formData[field.key];
    if (!value) return "";
    if (field.type === "month" && /^\d{4}-\d{2}$/.test(String(value))) {
      const [year, month] = String(value).split("-");
      return `${month}/${year}`;
    }
    return String(value);
  };

  const renderPreviewFieldCard = (field, overrideValue) => {
    if (HIDDEN_WORK_ADDRESS_FIELDS.has(field.key)) return null;
    const value = formatPreviewValue(field, overrideValue);
    if (!value) return null;

    return (
      <div
        key={field.key}
        className={`${field.key === "work_address_google_map" ? "min-[1100px]:col-span-2" : ""} flex items-center gap-3 px-4 py-4 border-b last:border-b-0`}
      >
        <div className="w-8 h-8 flex items-center justify-center rounded-md bg-gray-50 text-[#6938EF]">
          <img src={getPreviewIcon(field.key)} alt="" className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0 space-y-1">
          <div className="text-xs text-gray-500">{field.label}</div>
          <div className="text-sm font-medium text-gray-900 break-words">{value || "—"}</div>
        </div>
      </div>
    );
  };

  const renderCurrentStatusPreview = (label) => (
    <div className="min-[1100px]:col-span-2 px-4 py-4 border-b last:border-b-0">
      <div className="flex items-center justify-between rounded-2xl border border-gray-200 bg-white px-5 py-4 shadow-sm">
        <span className="text-sm font-semibold text-gray-900">{label}</span>
        <span className="relative inline-flex h-6 w-11 items-center rounded-full bg-[#6938EF]">
          <span className="absolute right-1 h-4 w-4 rounded-full bg-white shadow-sm" />
        </span>
      </div>
    </div>
  );
  

  // Renders a single form field based on its FIELD_DEFS entry
  const renderField = ({ key, label, type, options }) => {
    const isCurrent = Number(formData.work_current_job) === 1;
    const isEndDate = key === "work_end_date";
    const startMonthValue = String(formData.work_start_date || "").trim();
    const endMonthValue = String(formData.work_end_date || "").trim();
    const isCityRegionCountry = key === "city_region_country";
    const isAutoFilledAddressField = AUTO_FILLED_WORK_ADDRESS_FIELDS.has(key);
    const isVisibleRequired =
      (planType >= 2 && ["company_name", "work_experience_pin_code", "city_region_country", "job_title", "work_start_date", "job_function", "job_sub_functions"].includes(key)) ||
      (planType >= 3 && ["building_name", "work_address_google_map"].includes(key)) ||
      (planType >= 2 && ["work_address_city", "work_address_region", "work_address_country"].includes(key));
    const isRequired = requiredKeys.has(key) || (isEndDate && !isCurrent) || isVisibleRequired;
    const clearError = (k) => {
      if (errors[k]) setErrors((p) => ({ ...p, [k]: null }));
    };

    if (!isEditing && !hasVisibleFieldValue(key)) {
      return null;
    }

    if (HIDDEN_WORK_ADDRESS_FIELDS.has(key)) {
      return null;
    }

    if (key === "work_experience_pin_code") {
      const showPincodeDerivedToast = () => {
        const now = Date.now();
        if (now - (lastPincodeToastRef.current || 0) > 1000) {
          toast.dismiss();
          toast("Pincode is derived from Address. To change it, edit the Address field and it will update automatically.", {
            icon: "ℹ️",
          });
          lastPincodeToastRef.current = now;
        }
      };

      return (
        <div
          key={key}
          ref={(el) => (fieldRefs.current[key] = el)}
          onClick={() => {
            if (isEditing && isPincodeDisabledByAddress) {
              showPincodeDerivedToast();
            }
          }}
          className={isPincodeDisabledByAddress && isEditing ? "cursor-not-allowed" : undefined}
        >
          <TextField
            label={label}
            type="number"
            placeholder={isPincodeDisabledByAddress ? "Derived from Address" : "Click to select pincode"}
            name={key}
            value={formData[key] ?? ""}
            onChange={(e) => { handleChange(key, e.target.value); clearError(key); }}
            onClick={() => {
              if (!isEditing || isPincodeDisabledByAddress) return;
              setShowLocationModal(true);
            }}
            onFocus={(e) => {
              if (!isEditing) return;
              if (isPincodeDisabledByAddress) {
                e.target.blur();
                return;
              }
              e.target.blur();
              setShowLocationModal(true);
            }}
            readOnly
            disabled={!isEditing || isPincodeDisabledByAddress}
            rightIcon={isEditing && !isPincodeDisabledByAddress ? "../input-logos/location.svg" : undefined}
            title={isPincodeDisabledByAddress
              ? "Pincode is derived from Address. Edit the Address field to update it."
              : undefined}
            required={isRequired}
            error={errors[key]}
          />
        </div>
      );
    }

    if (type === "select") {
      return (
        <div key={key} ref={(el) => (fieldRefs.current[key] = el)}>
          <TextField
            label={label}
            name={key}
            required={isRequired}
            value={formData[key] ?? ""}
            onChange={(e) => { handleChange(key, e.target.value); clearError(key); }}
            disabled={!isEditing || isAutoFilledAddressField}
            select
            options={options}
            error={errors[key]}
          />
        </div>
      );
    }

    if (key === "work_current_job") {
      return null;
    }

    if (key === "work_address_google_map") {
      return (
        <div key={key} ref={(el) => (fieldRefs.current[key] = el)}>
          <TextField
            label={label}
            placeholder="Click to pick location from map"
            name={key}
            value={formData[key] ?? ""}
            onChange={(e) => { handleChange(key, e.target.value); clearError(key); }}
            onClick={openGoogleMapFromAddressField}
            onFocus={(e) => {
              if (!isEditing) return;
              e.target.blur();
              openGoogleMapFromAddressField();
            }}
            readOnly={isEditing}
            disabled={!isEditing}
            required={isRequired}
            error={errors[key]}
          />
        </div>
      );
    }

    if (isEndDate && isCurrent) {
      return null;
    }

    if (type === "phone") {
      return (
        <div key={key} ref={(el) => (fieldRefs.current[key] = el)}>
          <PhoneField
            label={label}
            name={key}
            value={formData[key] ?? ""}
            onChange={(e) => { handleChange(key, e.target.value); clearError(key); }}
            disabled={!isEditing}
            error={errors[key]}
            openCountryModal={() => isEditing && setShowCountryModal(true)}
            countryIso={selectedCountryIso}
          />
        </div>
      );
    }

    if (type === "date" || type === "month") {
      return (
        <div key={key} ref={(el) => (fieldRefs.current[key] = el)}>
          <TextField
            label={label}
            type={type}
            name={key}
            value={formData[key] ?? ""}
            onChange={(e) => { handleChange(key, e.target.value); setDateError(""); clearError(key); }}
            disabled={!isEditing || isAutoFilledAddressField || isCityRegionCountry}
            required={isRequired}
            min={isEndDate && formData.work_start_date ? formData.work_start_date : undefined}
            error={errors[key] || (isEndDate && dateError ? dateError : undefined)}
            rightIcon="../input-logos/calander.svg"
            placeholder="MM YYYY"
            minMonth={key === "work_end_date" ? startMonthValue : undefined}
            maxMonth={key === "work_start_date" && !isCurrent ? endMonthValue : undefined}
          />
        </div>
      );
    }

    if (type === "number") {
      return (
        <div key={key} ref={(el) => (fieldRefs.current[key] = el)}>
          <TextField
            label={label}
            type="number"
            placeholder={`Enter ${label}`}
            name={key}
            value={formData[key] ?? ""}
            onChange={(e) => { handleChange(key, e.target.value); clearError(key); }}
            disabled={!isEditing || isAutoFilledAddressField || isCityRegionCountry}
            required={isRequired}
            error={errors[key]}
          />
        </div>
      );
    }

    return (
      <div key={key} ref={(el) => (fieldRefs.current[key] = el)}>
        <TextField
          label={label}
          placeholder={`Enter ${label}`}
          name={key}
          value={formData[key] ?? ""}
          onChange={(e) => { handleChange(key, e.target.value); clearError(key); }}
          disabled={!isEditing || isAutoFilledAddressField || isCityRegionCountry}
          required={isRequired}
          error={errors[key]}
        />
      </div>
    );
  };

  const renderCurrentJobToggle = () => {
    if (!isEditing && Number(formData.work_current_job) !== 1) return null;

    return (
      <div className="w-full flex justify-between my-3 pr-2" ref={(el) => (fieldRefs.current["work_current_job"] = el)}>
      <label className="flex items-center gap-2 cursor-pointer mb-2 w-full justify-between">
        <span className="text-sm text-gray-500">Current Job<span className="text-red-500">*</span></span>
        <label className="relative inline-flex items-center cursor-pointer">
          <input
            type="checkbox"
            className="sr-only peer"
              checked={Number(formData.work_current_job) === 1}
            onChange={handleToggleCurrentSelect}
            disabled={!isEditing}
          />
          <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:bg-[#6938EF] after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:after:translate-x-[16px]" />
        </label>
      </label>
      {errors.work_current_job && <p className="text-sm text-red-500">{errors.work_current_job}</p>}
    </div>
    );
  };

   if (paymentStatusLoading) {
    return (
      <div className="flex justify-center items-center min-h-[40vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#7A5AF8]" />
      </div>
    );
  }

   if (!isPaymentActive) return (
    <div className="w-full flex items-center justify-center h-[90vh] p-6">
      <div className="max-w-5xl w-full bg-white rounded-2xl p-8 shadow-lg grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
        {/* Left column: text, perks, CTA */}
        <div className="px-2 md:px-6">
          <div className="w-20 h-20 rounded-full bg-[#F6F4FF] flex items-center justify-center mb-4">
            <svg width="48" height="48" viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <linearGradient id="g1a" x1="0" y1="0" x2="1" y2="1">
                  <stop offset="0%" stopColor="#BFA7FF"/>
                  <stop offset="100%" stopColor="#7B4DFF"/>
                </linearGradient>
              </defs>
              <circle cx="40" cy="55" r="15" fill="url(#g1a)"/>
              <circle cx="80" cy="55" r="15" fill="url(#g1a)"/>
            </svg>
          </div>
          <h3 className="text-3xl font-bold text-gray-800 mb-2">No Active Plan</h3>
          <p className="text-gray-600 mb-6">You don't have an active plan yet. Choose a plan to unlock all the features and make the most of  PhoneBookPlus.</p>

          <div className="grid grid-cols-3 gap-3 mb-6">
            <div className="flex flex-col items-center text-center p-3">
              <svg width="48" height="48" viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
                <defs>
                  <linearGradient id="icon1" x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor="#BFA7FF"/>
                    <stop offset="100%" stopColor="#7B4DFF"/>
                  </linearGradient>
                </defs>
                <circle cx="40" cy="55" r="15" fill="url(#icon1)"/>
                <circle cx="80" cy="55" r="15" fill="url(#icon1)"/>
                <path d="M35 25C50 10 70 10 85 25" stroke="#8D63FF" strokeWidth="6" strokeLinecap="round"/>
              </svg>
              <p className="text-xs text-gray-700 mt-2">Build stronger connections</p>
            </div>

            <div className="flex flex-col items-center text-center p-3">
              <svg width="48" height="48" viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
                <defs>
                  <linearGradient id="icon2" x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor="#CDB7FF"/>
                    <stop offset="100%" stopColor="#7B4DFF"/>
                  </linearGradient>
                </defs>
                <path d="M60 10L100 25V55C100 82 82 102 60 110C38 102 20 82 20 55V25L60 10Z" fill="url(#icon2)"/>
                <rect x="42" y="50" width="36" height="28" rx="6" fill="#8D63FF"/>
              </svg>
              <p className="text-xs text-gray-700 mt-2">Keep your data secure</p>
            </div>

            <div className="flex flex-col items-center text-center p-3">
              <svg width="48" height="48" viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
                <defs>
                  <linearGradient id="icon3" x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor="#D5C4FF"/>
                    <stop offset="100%" stopColor="#7B4DFF"/>
                  </linearGradient>
                </defs>
                <rect x="20" y="65" width="18" height="35" rx="4" fill="url(#icon3)"/>
                <rect x="48" y="50" width="18" height="50" rx="4" fill="url(#icon3)"/>
                <rect x="76" y="35" width="18" height="65" rx="4" fill="url(#icon3)"/>
              </svg>
              <p className="text-xs text-gray-700 mt-2">Boost productivity and grow</p>
            </div>
          </div>

          <button onClick={() => window.location.href = "/payment"} className="mt-2 inline-flex items-center gap-3 bg-[#6D5EF7] text-white px-6 py-3 rounded-full font-semibold shadow-lg hover:bg-[#5b49e8] transition">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
            Choose a Plan
          </button>
        </div>

        {/* Right column: illustration */}
        <div className="flex items-center justify-center p-4">
          <svg width="320" height="240" viewBox="0 0 320 240" fill="none" xmlns="http://www.w3.org/2000/svg" className="max-w-full h-auto">
            <defs>
              <linearGradient id="illGrad" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%" stopColor="#F3EEFF"/>
                <stop offset="100%" stopColor="#F8F6FF"/>
              </linearGradient>
            </defs>
            <rect x="20" y="20" width="220" height="200" rx="16" fill="url(#illGrad)" stroke="#EDE7FF" strokeWidth="2"/>
            <rect x="52" y="44" width="156" height="14" rx="7" fill="#FFFFFF"/>
            <rect x="52" y="72" width="156" height="12" rx="6" fill="#EDE7FF"/>
            <rect x="52" y="96" width="120" height="12" rx="6" fill="#EDE7FF"/>
            <circle cx="260" cy="40" r="28" fill="#F6EEFF"/>
          </svg>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {/* Delete Confirmation Modal */}
      {deleteModal.open &&
        ReactDOM.createPortal(
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30">
            <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-sm text-center">
              <div className="mb-4 flex justify-center flex-col items-center">
                <div className="h-14 w-14 flex mb-4 justify-center items-center bg-[linear-gradient(180deg,#8862F2_0%,#7544FC_29%,#40248B_100%)] rounded-full !p-0 shadow-sm transition-opacity">
                  <svg fill="#ffffff" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" className="w-10 h-10">
                    <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" />
                  </svg>
                </div>
                <h3 className="text-lg font-bold text-[#6938EF] mb-2">Delete Work Experience?</h3>
                <p className="text-gray-600">Are you sure you want to delete this work experience? This action cannot be undone.</p>
              </div>
              <div className="flex gap-4 justify-center mt-6">
                <button
                  className="px-6 py-2 rounded-full bg-gray-100 text-gray-700 font-semibold hover:bg-gray-200 transition"
                  onClick={() => setDeleteModal({ open: false, experienceId: null })}
                >
                  Cancel
                </button>
                <button className="px-6 py-2 rounded-full submit-button font-semibold" onClick={confirmDelete}>
                  Delete
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

      {addExperiance ? (
        <div>
          <div className="rounded-2xl p-5 md:p-6 border border-gray-100 shadow-sm">
            <div className="flex justify-between items-center">
              <div className="flex items-center">
                <button
                  aria-label="Back to previous page"
                  onClick={() => setAddExperiance(false)}
                  className="flex items-center aoboshi-one text-lg text-[#212121] font-medium p-4 rounded-2xl"
                >
                  <img src="./back-arrow.svg" alt="back-arrow" className="mr-4" />
                </button>
                <h2 className="text-lg font-semibold text-gray-900">
                  {selectedId ? "Edit Work Experience" : "Add Work Experience"}
                </h2>
              </div>
              <div className="flex gap-4 items-center">
                {isEditing ? (
                  <button
                    onClick={handleSubmit}
                    type="button"
                    disabled={submitting}
                    aria-busy={submitting}
                    className={`h-10 w-10 rounded-full flex items-center justify-center ${submitting ? "opacity-50 cursor-not-allowed" : ""}`}
                    aria-label={selectedId ? "Update work experience" : "Submit work experience"}
                  >
                    <img src="../tick_mark.svg" alt="Submit" className="w-6 h-6" />
                  </button>
                ) : (
                  <button onClick={() => setIsEditing(true)} type="button">
                    <img src="./edit.svg" alt="Edit" className="w-5 h-5" />
                  </button>
                )}
              </div>
            </div>

            <div className="h-[85dvh] overflow-y-auto [&::-webkit-scrollbar]:hidden" ref={formScrollRef}>
              <div className="mt-4 gap-4">
                <div className="mb-4 border border-gray-100 rounded-xl bg-white overflow-visible">
                  <button
                    type="button"
                    onClick={() => setOpenWorkDetails((prev) => !prev)}
                    className={`w-full flex items-center justify-between px-5 py-4 transition-all ${
                      openWorkDetails
                        ? "bg-gradient-to-r from-[#6938EF]/5 to-transparent border-b border-[#E6E2FF]"
                        : "bg-gradient-to-r from-gray-50 to-white"
                    }`}
                  >
                    <span className={`font-semibold transition-colors ${openWorkDetails ? "text-[#6938EF]" : "text-gray-900"}`}>
                      Work Details
                    </span>
                    <span className={`transition-transform duration-300 ${openWorkDetails ? "rotate-180 text-[#6938EF]" : "text-gray-600"}`}>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </span>
                  </button>

                  {openWorkDetails && (
                    <div className={`${isEditing ? "grid gap-4 px-4 pb-4 mt-4 grid-cols-1" : "grid grid-cols-1 min-[1100px]:grid-cols-2 gap-0 px-4 pb-4"}`}>
                      {isEditing ? (
                        <>
                          {renderCurrentJobToggle()}
                          {FIELD_DEFS
                            .filter((f) => visibleWorkDetailsFields.includes(f.key))
                            .map(renderField)}
                        </>
                      ) : (
                        <>
                          {Number(formData.work_current_job) === 1 &&
                            renderCurrentStatusPreview("Current Job")}
                          {FIELD_DEFS
                            .filter((f) => visibleWorkDetailsFields.includes(f.key))
                            .filter((f) => hasVisibleFieldValue(f.key))
                            .map((f) => renderPreviewFieldCard(f))}
                        </>
                      )}
                    </div>
                  )}
                </div>

                {shouldShowWorkAddressSection && (
                <div className="mb-4 border border-gray-100 rounded-xl bg-white overflow-visible">
                  <button
                    type="button"
                    onClick={() => setOpenWorkAddress((prev) => !prev)}
                    className={`w-full flex items-center justify-between px-5 py-4 transition-all ${
                      openWorkAddress
                        ? "bg-gradient-to-r from-[#6938EF]/5 to-transparent border-b border-[#E6E2FF]"
                        : "bg-gradient-to-r from-gray-50 to-white"
                    }`}
                  >
                    <span className={`font-semibold transition-colors ${openWorkAddress ? "text-[#6938EF]" : "text-gray-900"}`}>
                      Work Address
                    </span>
                    <span className={`transition-transform duration-300 ${openWorkAddress ? "rotate-180 text-[#6938EF]" : "text-gray-600"}`}>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </span>
                  </button>

                  {openWorkAddress && (
                    <div className={`${isEditing ? "grid gap-4 px-4 pb-4 mt-4 grid-cols-1" : "grid grid-cols-1 min-[1100px]:grid-cols-2 gap-0 px-4 pb-4"}`}>
                      {FIELD_DEFS
                        .filter((f) => visibleWorkAddressFields.includes(f.key))
                        .filter((f) => isEditing || hasVisibleFieldValue(f.key))
                        .map((f) => (isEditing ? renderField(f) : renderPreviewFieldCard(f)))}
                    </div>
                  )}
                </div>
                )}
              </div>


            </div>
          </div>

          {showLocationModal && (
            <LocationModal
              show={showLocationModal}
              onClose={() => setShowLocationModal(false)}
              onSelect={handlePlaceSelect}
              initialPincode={formData.work_experience_pin_code || ""}
              initialPlaceName={formData.company_name || ""}
            />
          )}

          {showGoogleMapPicker && (
            <>
              <GoogleMapPicker
                show={showGoogleMapPicker}
                onClose={() => { setShowGoogleMapPicker(false); setMapInitialSearch(""); }}
                onSelect={handleSelectLocation}
                initialSearch={mapInitialSearch}
              />
              {/* <MapplsMapPicker
                show={showGoogleMapPicker}
                onClose={() => { setShowGoogleMapPicker(false); setMapInitialSearch(""); }}
                onSelect={handleSelectLocation}
                initialSearch={mapInitialSearch}
              /> */}
            </>
          )}

          {showCountryModal && (
            <CountryModel
              onSelect={(c) => {
                localStorage.setItem("selectedCountry", c.code);
                localStorage.setItem("selectedDialCode", `+${c.dialCode}`);
                setSelectedCountryIso(c.code);
                setFormData((prev) => ({ ...prev, contact_number: String(c.dialCode) }));
                setShowCountryModal(false);
              }}
              onClose={() => setShowCountryModal(false)}
            />
          )}
        </div>
      ) : (
        <div className="rounded-2xl p-5 md:p-6 border border-gray-100 shadow-sm">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-lg font-semibold text-gray-900">Work Experience</h2>
            {planType >= 2 && (
              <button
                onClick={handleAddClick}
                className="p-2 hover:bg-gray-100 rounded-full bg-white shadow-md"
              >
                <img src="./add.svg" alt="Add" className="w-5 h-5" />
              </button>
            )}
          </div>

          <div className="space-y-6 h-[80dvh] overflow-y-auto py-5 px-3 [&::-webkit-scrollbar]:hidden">
            {planType === 1 ? (
              <div className="flex flex-col h-[70dvh] justify-center items-center text-center">
                <img src="./no-data/no-work.svg" alt="Not available" className="mx-auto mb-4 h-40 w-40" />
                <p className="font-semibold text-[20px]">Not available in {getPlanDisplayName("Basic")} plan</p>
                <p className="text-gray-500 mt-2">Upgrade to {getPlanDisplayName("Basic Plus")} or {getPlanDisplayName("Basic Pro")} to add your work experience.</p>
              </div>
            ) : loadingExperiences ? (
              Array.from({ length: 3 }).map((_, index) => (
                <div
                  key={index}
                  className="relative rounded-2xl border-gray-200 p-4 sm:p-6 md:p-8 bg-[#F7F8F8] shadow-[6px_6px_4px_0_rgba(122,90,248,0.12)] animate-pulse"
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pr-12 sm:pr-14">
                    <div className="space-y-4">
                      <div className="h-4 w-32 rounded bg-gray-200" />
                      <div className="h-3 w-48 rounded bg-gray-200" />
                      <div className="h-4 w-36 rounded bg-gray-200" />
                      <div className="h-3 w-56 rounded bg-gray-200" />
                    </div>
                    <div className="space-y-4">
                      <div className="h-4 w-28 rounded bg-gray-200" />
                      <div className="h-3 w-40 rounded bg-gray-200" />
                      <div className="h-4 w-32 rounded bg-gray-200" />
                      <div className="h-3 w-44 rounded bg-gray-200" />
                    </div>
                  </div>
                </div>
              ))
            ) : experiences.length > 0 ? (
              experiences.map((exp, index) => (
                <div
                  key={exp.experience_id || index}
                  onClick={() => handleCardClick(exp)}
                  className={`group relative rounded-2xl border-gray-200 p-4 sm:p-6 md:p-8 bg-[#F7F8F8] transition shadow-[6px_6px_4px_0_rgba(122,90,248,0.29)] ${Number(exp.work_current_job) === 1 ? "pl-8 sm:pl-10 md:pl-8" : ""}`}
                >
                  {Number(exp.work_current_job) === 1 && (
                    <div className="absolute h-5 w-5 sm:h-6 sm:w-6 top-2 sm:top-3 left-2 sm:left-3 flex justify-center items-center bg-[#6938EF] rounded-[8px] sm:rounded-[12px] p-1 shadow-sm">
                      <svg fill="#ffffff" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" stroke="#ffffff">
                        <g id="SVGRepo_iconCarrier">
                          <path d="M16.948 20.2a1 1 0 0 1-.465-.115l-4.472-2.352-4.471 2.348a1 1 0 0 1-1.451-1.054l.854-4.979-3.619-3.527a1 1 0 0 1 .554-1.706l5-.727 2.237-4.531A1 1 0 0 1 12.011 3a1 1 0 0 1 .9.558l2.236 4.53 5 .727a1 1 0 0 1 .554 1.706l-3.621 3.526.854 4.98a1 1 0 0 1-.986 1.173zM6.171 10.5l2.544 2.479a1 1 0 0 1 .285.888l-.6 3.5 3.144-1.653a.993.993 0 0 1 .931 0l3.144 1.653-.6-3.5a1 1 0 0 1 .288-.885l2.544-2.482-3.515-.511a1 1 0 0 1-.752-.547l-1.573-3.183-1.573 3.186a1 1 0 0 1-.752.547z" />
                        </g>
                      </svg>
                    </div>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 sm:gap-3 md:gap-4 pr-12 sm:pr-14">
                    <div className="space-y-2 sm:space-y-3 md:space-y-4">
                      <div>
                        <label className="block text-xs sm:text-sm md:text-lg font-semibold text-black mb-0.5 sm:mb-1">Company Name</label>
                        <p className="text-xs sm:text-sm text-black truncate">{exp.company_name || "—"}</p>
                      </div>
                      {formatWorkAddress(exp) !== "—" && (
                        <div>
                          <label className="block text-xs sm:text-sm md:text-lg font-semibold text-black mb-0.5 sm:mb-1">Company Location</label>
                          <p className="text-xs sm:text-sm text-black">{formatWorkAddress(exp)}</p>
                        </div>
                      )}
                    </div>
                    <div className="space-y-2 sm:space-y-3 md:space-y-4">
                      <div>
                        <label className="block text-xs sm:text-sm md:text-lg font-semibold text-black mb-0.5 sm:mb-1">Job Title</label>
                        <p className="text-xs sm:text-sm text-black truncate">{exp.job_title || "—"}</p>
                      </div>
                      <div>
                        <label className="block text-xs sm:text-sm md:text-lg font-semibold text-black mb-0.5 sm:mb-1">Duration of Work</label>
                        <p className="text-xs sm:text-sm text-black">{formatWorkDuration(exp)}</p>
                      </div>
                    </div>
                  </div>

                  {/* Delete button */}
                  <button
                    onClick={(e) => handleDelete(e, exp.experience_id)}
                    className="absolute top-2 sm:top-3 right-2 sm:right-3 h-5 w-5 sm:h-6 sm:w-6 flex justify-center items-center bg-[#6B47F5] hover:bg-[#5A3EE3] rounded-[8px] sm:rounded-[12px] p-0.5 sm:p-1 shadow-sm opacity-0 group-hover:opacity-100 transition-opacity"
                    title="Delete"
                  >
                    <svg fill="#ffffff" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" className="w-3 h-3 sm:w-4 sm:h-4">
                      <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" />
                    </svg>
                  </button>

                  {/* Colleague avatars */}
                  <div className="absolute bottom-2 sm:bottom-3 right-2 sm:right-3 flex items-center rounded-[8px] sm:rounded-[12px] pl-1 sm:pl-2 pr-0.5 sm:pr-1 py-0.5 sm:py-1">
                    <div className="flex -space-x-1.5 sm:-space-x-2 mr-1 sm:mr-2">
                      {exp.user_work_me?.slice(0, 3).map((user, idx) => (
                        <div
                          key={idx}
                          title={user.name}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (user.profile_image) {
                              setImagePreview({
                                url: `${process.env.REACT_APP_API_BASE_URL}${user.profile_image}`,
                                name: user.name,
                              });
                            }
                          }}
                          className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 rounded-full overflow-hidden border border-sm:border-2 border-white cursor-pointer hover:scale-105 transition"
                        >
                          {user.profile_image ? (
                            <img
                              src={`${process.env.REACT_APP_API_BASE_URL}${user.profile_image}`}
                              alt={user.name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full bg-[#7A5AF8] text-white flex items-center justify-center text-xs sm:text-sm font-semibold">
                              {getInitials(user.name)}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                    <button
                      onClick={(e) => { e.stopPropagation(); setSelectedExperience(exp); setShowViewModal(true); }}
                      className="w-6 h-6 sm:w-7 sm:h-7 md:w-7 md:h-7 bg-[#6B47F5] rounded-full flex items-center justify-center hover:bg-[#5A3EE3] transition"
                    >
                      <span className="text-white text-sm sm:text-base md:text-lg font-bold leading-none">+</span>
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <div className="flex flex-col h-[70dvh] justify-center items-center">
                <img src="./no-data/no-work.svg" alt="No data" className="mx-auto mb-4 h-40 w-40" />
                <p className="font-semibold text-[28px] text-center">No work experience added yet.</p>
                <p className="text-gray-500 text-center">This user hasn't added work experience yet.</p>
              </div>
            )}
          </div>
        </div>
      )}

      {showViewModal && selectedExperience && (
        <ViewColleagueModal
          experience={selectedExperience}
          type="work"
          onSuccess={fetchExperiences}
          endpointAdd="/set_contact_add_work_experience"
          onClose={() => setShowViewModal(false)}
        />
      )}

      {imagePreview &&
        ReactDOM.createPortal(
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75 backdrop-blur-sm p-4"
            onClick={() => setImagePreview(null)}
          >
            <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
              <div className="flex flex-col items-center gap-4">
                <div className="h-64 w-64 rounded-full overflow-hidden border-4 border-gray-200 flex items-center justify-center bg-gray-100">
                  <img
                    src={imagePreview.url}
                    onError={(e) => { e.target.src = "../chatbox/default.png"; }}
                    alt={imagePreview.name}
                    className="h-full w-full object-cover"
                  />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 text-center">{imagePreview.name}</h3>
                <button className="w-full submit-button" onClick={() => setImagePreview(null)}>Close</button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </>
  );
};
